import type { ShiftType } from '@lmc/ts-common'
import {
  FormCard,
  FormItem,
  GroupedUsersMultiSelect,
  Input,
  MultiSelect,
  SelectAddOption
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { InputNumber } from 'antd'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getRequiredRule } from 'screens/Rostering/utils'
import { styled } from 'styled-components'
import { commonPatterns } from 'utils/enhancedTestIds'
import { cleanDecimalValues } from '../../formComponents/utils'

type ShiftTypeValue = {
  id: string | undefined
  name: string
}

type ShiftFormCardProps = {
  testId?: string
  shiftTypes?: ShiftType[]
  users?: any
  formData?: any
  form?: any
  setFormData: (data: any) => void
  isSubmitting?: boolean
}

const ShiftFormCard: FC<ShiftFormCardProps> = ({
  testId,
  shiftTypes,
  users,
  formData,
  form,
  setFormData,
  isSubmitting = false
}) => {
  const { t } = useTranslation()
  const baseId = testId || 'shift-form-card'
  const [shiftTypeValue, setShiftTypeValue] = useState<
    ShiftTypeValue | undefined
  >(undefined)
  const [showValidation, setShowValidation] = useState(false)

  const shiftTypeOptions =
    shiftTypes?.map((shift) => ({
      value: {
        id: shift.id,
        name: shift.name
      },
      label: shift.name
    })) || []

  useEffect(() => {
    if (formData?.instanceTypeId && !shiftTypeValue) {
      const foundType = shiftTypeOptions.find(
        (option) => option.value.id === formData.instanceTypeId
      )
      if (foundType) {
        setShiftTypeValue(foundType.value)
      }
    }
  }, [formData?.instanceTypeId, shiftTypeOptions, shiftTypeValue])

  useEffect(() => {
    if (isSubmitting) {
      setShowValidation(true)
    }
  }, [isSubmitting])

  useEffect(() => {
    if (shiftTypeValue) {
      setShowValidation(false)
    }
  }, [shiftTypeValue])

  const handleShiftTypeChange = (value: ShiftTypeValue) => {
    setShiftTypeValue(value)

    const isNewShiftType = !shiftTypeOptions.some(
      (option) =>
        option.value.id === value.id && option.value.name === value.name
    )

    if (isNewShiftType) {
      setFormData((prev: any) => ({
        ...prev,
        newInstanceType: {
          name: value.name
        },
        instanceTypeId: undefined
      }))
    } else if (value && value.id) {
      setFormData((prev: any) => ({
        ...prev,
        instanceTypeId: value.id,
        newInstanceType: undefined
      }))

      form?.setFieldValue('instanceTypeId', value.id)
    } else {
      setFormData((prev: any) => ({
        ...prev,
        instanceTypeId: undefined,
        newInstanceType: undefined
      }))
    }
  }

  return (
    <FormCard level={2} padding="16px" data-testid={baseId}>
      <FormItem
        data-testid={commonPatterns.field(baseId, 'shift-type')}
        label={
          <MandatoryText>
            <RedText>*</RedText>
            <Text>{t('TYPE_OF_SHIFT')}</Text>
          </MandatoryText>
        }
        rules={[getRequiredRule(t('TYPE_OF_SHIFT'))]}
        validateStatus={showValidation && !shiftTypeValue ? 'error' : ''}
        help={
          showValidation && !shiftTypeValue
            ? t('TYPE_OF_SHIFT') + ' is required'
            : ''
        }
      >
        <SelectAddOption<ShiftTypeValue>
          data-testid={commonPatterns.input(baseId, 'shift-type')}
          options={shiftTypeOptions}
          selectedValue={shiftTypeValue}
          setSelectedValue={handleShiftTypeChange}
          valueKey="id"
          fields={[
            {
              key: 'name',
              placeholder: t('TYPE_NEW_SHIFT_TYPE'),
              type: 'text',
              size: 100
            }
          ]}
        />
      </FormItem>

      <FormItem name="instanceTypeId" hidden>
        <input type="hidden" />
      </FormItem>

      <FormItem
        data-testid={commonPatterns.field(baseId, 'title')}
        name="title"
        label={<Text>{t('TITLE')}</Text>}
        rules={[getRequiredRule(t('TITLE'))]}
      >
        <Input data-testid={commonPatterns.input(baseId, 'title')} />
      </FormItem>
      <FormItem
        data-testid={commonPatterns.field(baseId, 'description')}
        name="slotNotes"
        label={<Text>{t('DESCRIPTION')}</Text>}
      >
        <Input data-testid={commonPatterns.input(baseId, 'description')} />
      </FormItem>
      <InlineFormItem data-testid={`${baseId}-team-members-section`}>
        <NumberSupplyFormItem
          data-testid={commonPatterns.field(baseId, 'number-supply')}
          labelCol={labelColStyle}
          labelAlign="left"
          name="numberSupply"
          label={<Text>{t('TEAM_MEMBER(S)')}</Text>}
          rules={[getRequiredRule(t('TEAM_MEMBERS_REQUIRED'))]}
        >
          <InputNumber
            data-testid={commonPatterns.input(baseId, 'number-supply')}
            style={{ width: 72 }}
            min={1}
            max={50}
            type="number"
            formatter={cleanDecimalValues}
          />
        </NumberSupplyFormItem>
        <TeamMembersFormItem
          data-testid={commonPatterns.field(baseId, 'team-members')}
          name="teamMembers"
        >
          <GroupedUsersMultiSelect
            data-testid={commonPatterns.input(baseId, 'team-members')}
            users={users}
            title={t('SELECT_TEAM_MEMBERS')}
            values={formData?.teamMembers || []}
            hasClear
            hasSearch
            hasSelectAll
            placeholder
            maxTagCount={0}
            suffixIcon={
              <Text>
                {formData?.teamMembers?.length ?? 0}/
                {formData?.numberSupply ?? 0}
              </Text>
            }
          />
        </TeamMembersFormItem>
      </InlineFormItem>
    </FormCard>
  )
}

const labelColStyle = {
  style: {
    width: 200
  }
}

const NumberSupplyFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }

  .ant-form-item-label > label {
    color: ${({ theme }) => theme.color.neutral.color4};
  }
  width: 200px;
`

const TeamMembersFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
  flex: 1;
  min-width: 0;
`

const InlineFormItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 130px;
`
const MandatoryText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const RedText = styled.span`
  color: red;
`

export default ShiftFormCard
